import { useGSAP } from "@gsap/react";
import { gsap } from "gsap/dist/gsap";
import React, { useRef } from "react";

import { BigHeroBlock as Value } from "@reactivated";

import {
    HeroLayoutProps,
    HeroLayoutSettings,
    TextAlignment,
} from "@thelabnyc/thelabui/src/components/Flexihero/HeroLayout.interfaces";
import { HeroSet } from "@thelabnyc/thelabui/src/components/Flexihero/HeroSet";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { MediaChooserBlock } from "../MediaChooserBlock";
import { RichText } from "../RichText";

import styles from "./index.module.scss";

export interface Props {
    media: React.JSX.Element | null;
    content: React.JSX.Element | null;
    content_alignment: Value["content_alignment"];
    text_color: Value["text_color"];
    overlay: Value["overlay"];
    animation: Value["animation"];
}

interface ContentAlignment {
    contentAlignPrimary: HeroLayoutSettings["contentAlignPrimary"];
    contentAlignSecondary: HeroLayoutSettings["contentAlignSecondary"];
}

export const BigHero = ({
    content,
    media,
    content_alignment,
    text_color,
    overlay,
    animation,
}: Props) => {
    const ref = useRef<HTMLDivElement>(null);
    const innerRef = useRef<HTMLDivElement>(null);

    useGSAP(() => {
        if (
            !ref.current ||
            !innerRef.current ||
            animation !== "animateCurtains"
        )
            return;

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: ref.current,
                start: "top 50%",
                toggleActions: "play reverse play reverse",
            },
        });

        tl.fromTo(
            innerRef.current,
            { clipPath: "inset(20%)" },
            { clipPath: "inset(0%)", ease: "expo.out", duration: 1 },
        );
    }, []);

    const sharedStyles = {
        rootClassName: concatClassNames([
            text_color === "dark" ? styles.rootDarkText : styles.rootLightText,
            media === null ? styles.gradient : undefined,
        ]),
        mediaClassName: overlay ? styles[overlay] : undefined,
        contentClassName: styles.content,
    };

    const getAlignment: () => ContentAlignment = () => {
        switch (content_alignment) {
            case "top-left":
                return {
                    contentAlignPrimary: "left/top",
                    contentAlignSecondary: "left/top",
                };
            case "bottom-left":
                return {
                    contentAlignPrimary: "right/bottom",
                    contentAlignSecondary: "left/top",
                };
            case "middle-left":
                return {
                    contentAlignPrimary: "middle",
                    contentAlignSecondary: "left/top",
                };
            default:
                return {
                    contentAlignPrimary: "middle",
                    contentAlignSecondary: "middle",
                };
        }
    };

    const getTextAlignment: () => TextAlignment = () => {
        switch (content_alignment) {
            case "top-left":
            case "bottom-left":
            case "middle-left":
                return "left";
            default:
                return "center";
        }
    };

    const desktopSettings: HeroLayoutProps = {
        content,
        media,
        settings: {
            heroDirection: "row",
            heroPlacement: "backgroundFull",
            mediaPlacement: "background",
            mediaAspectRatio: "stretch",
            textAlignment: getTextAlignment(),
            contentWidth: 6,
            // TODO either change the interface to allow more than just `number`
            // for minHeight/maxHeight, or else rethink this entirely
            minHeight: "100vh" as unknown as number,
            ...getAlignment(),
        },
        styles: sharedStyles,
    };

    const laptopSettings: HeroLayoutProps = {
        ...desktopSettings,
        settings: {
            ...desktopSettings.settings,
            contentWidth: 6,
        },
    };

    const tabletSettings: HeroLayoutProps = {
        ...desktopSettings,
        settings: {
            ...desktopSettings.settings,
            contentWidth: 8,
        },
    };

    return (
        <div ref={ref}>
            <div ref={innerRef}>
                <HeroSet
                    layouts={{
                        desktop: desktopSettings,
                        laptop: laptopSettings,
                        tablet: tabletSettings,
                        mobile: {
                            content,
                            media,
                            settings: {
                                heroDirection: "row",
                                heroPlacement: "backgroundFull",
                                mediaPlacement: "background",
                                mediaAspectRatio: "stretch",
                                textAlignment: getTextAlignment(),
                                contentWidth: 12,
                                minHeight: "100vh" as unknown as number,
                                ...getAlignment(),
                            },
                            styles: sharedStyles,
                        },
                    }}
                />
            </div>
        </div>
    );
};

export const BigHeroBlock = ({ value }: { value: Value }) => {
    const content = value.content ? (
        <RichText
            value={value.content}
            largeHeadings={value.text_size === "textSizeLarge"}
        />
    ) : null;

    const media =
        value.media && value.media.length > 0 ? (
            <MediaChooserBlock value={value.media} />
        ) : null;

    return (
        <BigHero
            content={content}
            media={media}
            content_alignment={value.content_alignment}
            text_color={value.text_color}
            overlay={value.overlay}
            animation={value.animation}
        />
    );
};
